import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { getReviews } from "@/lib/api";

export function Stars({ value, size = "w-4 h-4" }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`${size} ${n <= Math.round(value) ? "fill-current text-primary-gold" : "text-white/25"}`}
        />
      ))}
    </div>
  );
}

export function useBookReviews(productId) {
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    getReviews(productId).then(setReviews).catch(() => setReviews([]));
  }, [productId]);
  return reviews;
}

export function RatingSummary({ reviews, productId }) {
  const avg = reviews.length ? reviews.reduce((s, r) => s + r.rating, 0) / reviews.length : 0;
  return (
    <Link
      to={`/reviews?book=${productId}`}
      data-testid="rating-summary"
      className="mt-6 flex items-center gap-3 text-sm text-soft hover:text-primary-gold transition-colors"
    >
      <Stars value={avg} />
      {reviews.length ? (
        <span data-testid="rating-summary-text">
          {avg.toFixed(1)} · {reviews.length} {reviews.length === 1 ? "review" : "reviews"}
        </span>
      ) : (
        <span data-testid="rating-summary-text">No reviews yet — be the first</span>
      )}
    </Link>
  );
}

export function LatestReviews({ reviews, productId }) {
  return (
    <div className="mt-8" data-testid="book-latest-reviews">
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary-gold">Reader Reviews</h3>
        <Link
          to={`/reviews?book=${productId}`}
          data-testid="book-reviews-link"
          className="group inline-flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-soft hover:text-primary-gold transition-colors"
        >
          {reviews.length ? "Read all · Write one" : "Write a review"}
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
        </Link>
      </div>
      {reviews.length === 0 ? (
        <p className="mt-4 text-soft text-sm" data-testid="book-no-reviews">
          No reviews yet. Read it? Share your thoughts with other readers.
        </p>
      ) : (
        <div className="mt-4 space-y-4">
          {reviews.slice(0, 3).map((r) => (
            <div key={r.id} data-testid="book-review-item" className="border border-white/10 bg-[#121212]/80 p-5">
              <div className="flex items-center gap-3">
                <span className="font-serif-display text-lg text-text-primary">{r.name}</span>
                <Stars value={r.rating} size="w-3.5 h-3.5" />
              </div>
              <p className="mt-3 text-soft text-sm leading-relaxed line-clamp-4">{r.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
